import { Link } from "react-router-dom";
import { Check, ArrowRight, Wind, Droplets, Sun, Truck, Sparkles, Users, Zap, Battery, MapPin } from "lucide-react";
import { ParallaxBg, ParallaxImage, FadeUp } from "@/components/Parallax";
import { IMG } from "@/lib/images";

export default function Trailers() {
  return (
    <div>
      {/* Hero parallax — dramatic Zion sky shot */}
      <ParallaxBg image={IMG.exteriorZion} minHeight="min-h-[80vh]" overlay="bg-gradient-to-b from-black/30 via-black/10 to-black/70" speed={0.28}>
        <div className="mx-auto max-w-6xl h-full px-6 md:px-10 lg:px-16 flex items-end pb-16 md:pb-20 min-h-[80vh]">
          <FadeUp className="text-ivory">
            <div className="overline text-ivory/70">Our Trailers</div>
            <h1 className="h-display mt-3 text-5xl md:text-7xl leading-[1.02]">
              Two matching<br/>three-stall trailers.
            </h1>
            <p className="mt-6 text-ivory/85 text-lg max-w-2xl leading-relaxed">
              Designed for weddings, concerts, festivals, corporate events, city gatherings, private parties, and other special occasions.
            </p>
          </FadeUp>
        </div>
      </ParallaxBg>

      {/* Real interior showcase */}
      <section className="section">
        <div className="mx-auto max-w-6xl grid md:grid-cols-2 gap-8">
          <FadeUp>
            <TrailerShowcase image={IMG.interiorSink} title="Interior · Sink Detail" caption="SUER-branded amenities, chrome fixtures, and porcelain finishes." />
          </FadeUp>
          <FadeUp delay={0.1}>
            <TrailerShowcase image={IMG.interiorWide} title="Interior · Overview" caption="Bright, ventilated, and thoughtfully finished for every guest." />
          </FadeUp>
        </div>
      </section>

      {/* Exterior parallax showcase — Zion sky shot */}
      <section className="pb-24 px-6 md:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <FadeUp>
            <div className="rounded-3xl overflow-hidden border border-stone">
              <img src={IMG.exteriorZion} alt="Southern Utah Event Rentals trailer at Zion" className="w-full h-auto" />
              <div className="p-8 bg-white grid md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-2">
                  <div className="overline">Exterior · On location</div>
                  <h3 className="h-display text-3xl mt-2">Ready for anywhere.</h3>
                  <p className="text-graphite text-sm mt-3 leading-relaxed">
                    Delivered by our team with the quiet generator on board — so you can host under the red-rock sky or downtown, without missing a beat.
                  </p>
                </div>
                <a href="tel:+14352889909" className="btn-secondary md:justify-self-end">Call 435.288.9909</a>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Feature strip */}
      <section className="section bg-sand">
        <div className="mx-auto max-w-6xl">
          <FadeUp>
            <div className="overline">Included with every trailer</div>
            <h2 className="h-display mt-3 text-4xl md:text-5xl">Every detail, considered.</h2>
          </FadeUp>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { i: Sparkles, t: "Three private stalls", d: "Fully enclosed, luxurious finishes." },
              { i: Wind, t: "Climate control", d: "Comfortable in summer heat or evening chill." },
              { i: Droplets, t: "Running water & sinks", d: "Fresh water, mirrors, and elegant hardware." },
              { i: Sun, t: "Interior lighting", d: "Warm, flattering lighting for guests." },
              { i: Zap, t: "Quiet generator", d: "Low-noise power so nothing interrupts your event." },
              { i: Battery, t: "Solar & city hookups", d: "Ready for grid, off-grid, or fully rural setups." },
              { i: Truck, t: "Professional delivery", d: "Delivered, set up, and picked up by our team." },
              { i: Users, t: "Guest capacity guidance", d: "Up to ~150 per trailer, 300 for both." },
              { i: MapPin, t: "Ready for anywhere", d: "Zion backdrops, private ranches, or downtown venues." },
            ].map((f, idx) => (
              <FadeUp key={f.t} delay={idx * 0.04}>
                <div className="card-soft p-7 h-full">
                  <f.i className="h-5 w-5 text-pebble" />
                  <div className="h-display text-xl mt-4">{f.t}</div>
                  <p className="text-graphite text-sm mt-2 leading-relaxed">{f.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax exterior breakaway */}
      <ParallaxBg image={IMG.heroExterior} minHeight="min-h-[70vh]" overlay="bg-gradient-to-r from-black/70 via-black/20 to-transparent" speed={0.28}>
        <div className="mx-auto max-w-6xl h-full px-6 md:px-10 lg:px-16 flex items-center min-h-[70vh]">
          <FadeUp className="max-w-xl text-ivory">
            <div className="overline text-ivory/70">On site</div>
            <h2 className="h-display mt-4 text-4xl md:text-6xl leading-[1.05]">Delivered anywhere your event takes you.</h2>
            <p className="mt-6 text-ivory/80 text-lg leading-relaxed">Three doors. Three private stalls per trailer. Quiet operation. And a footprint small enough to place discreetly at any venue.</p>
          </FadeUp>
        </div>
      </ParallaxBg>

      {/* Choice cards */}
      <section className="section">
        <div className="mx-auto max-w-6xl grid md:grid-cols-2 gap-8">
          <div className="card-soft p-10">
            <div className="overline">Best for smaller and mid-sized gatherings</div>
            <h3 className="h-display text-3xl md:text-4xl mt-3">One Trailer</h3>
            <ul className="mt-6 space-y-3 text-sm">
              {["1 luxury restroom trailer", "3 private stalls", "Delivery", "Setup", "Pickup"].map(f => (
                <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-pebble" /> {f}</li>
              ))}
            </ul>
            <Link to="/booking?trailers=1" data-testid="trailers-request-one" className="btn-primary mt-8 w-full">Request One Trailer <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="card-soft p-10 ring-1 ring-pebble">
            <div className="overline">Best for larger events or split restroom locations</div>
            <h3 className="h-display text-3xl md:text-4xl mt-3">Two Trailers</h3>
            <ul className="mt-6 space-y-3 text-sm">
              {["2 luxury restroom trailers", "6 total private stalls", "Delivery", "Setup", "Pickup"].map(f => (
                <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-pebble" /> {f}</li>
              ))}
            </ul>
            <Link to="/booking?trailers=2" data-testid="trailers-request-two" className="btn-primary mt-8 w-full">Request Both Trailers <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function TrailerShowcase({ image, title, caption }) {
  return (
    <div className="card-soft overflow-hidden">
      <div className="relative h-[480px] overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
      </div>
      <div className="p-8">
        <div className="overline">Three-Stall Luxury Restroom Trailer</div>
        <h3 className="h-display text-3xl mt-3">{title}</h3>
        <p className="text-graphite text-sm mt-3 leading-relaxed">{caption}</p>
      </div>
    </div>
  );
}
